import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  FaShieldHalved,
  FaTriangleExclamation,
  FaCreditCard,
  FaGift,
  FaGamepad,
  FaMobileScreenButton,
  FaHeadset,
  FaGear,
  FaLock,
  FaFileLines,
  FaDesktop,
  FaChartLine,
  FaCircleCheck,
} from 'react-icons/fa6';
import RgCallout from '../components/rgcallout';
import './innerpage.css';
import './howitworks.css';

const STEPS = [
  {
    number: '01',
    title: 'We Verify the Licence',
    text: 'Every casino we review must hold an active licence from the UK Gambling Commission. We check the UKGC public register directly and reject any site that cannot be verified.',
  },
  {
    number: '02',
    title: 'We Test the Casino Ourselves',
    text: 'Our reviewers create real accounts, make real deposits, and test games, bonuses, and withdrawal processes. We do not rely solely on operator-supplied information.',
  },
  {
    number: '03',
    title: 'We Assess Bonuses Fairly',
    text: 'We compare wagering requirements, time limits, eligible games, and bet restrictions against industry benchmarks. A large headline figure with harsh terms scores lower than a modest offer with fair conditions.',
  },
  {
    number: '04',
    title: 'We Score on 12 Criteria',
    text: 'Our proprietary scoring model covers: licence validity, game library, bonus value, payment options, mobile experience, customer support, responsible gambling tools, UX, software providers, security, transparency, and complaints record.',
  },
  {
    number: '05',
    title: 'We Assign a Star Rating',
    text: 'Each casino receives a star rating out of 5, calculated from the weighted average of all 12 criteria scores. Licence and responsible gambling tools carry the highest weighting.',
  },
  {
    number: '06',
    title: 'We Review Regularly',
    text: 'Casinos change. We revisit every review at least once per quarter to check for licence changes, new terms, or shifts in service quality.',
  },
];

const CRITERIA = [
  { label: 'Licence & Regulation', weight: 'Very High', Icon: FaShieldHalved },
  { label: 'Responsible Gambling Tools', weight: 'Very High', Icon: FaTriangleExclamation },
  { label: 'Payment Methods & Speed', weight: 'High', Icon: FaCreditCard },
  { label: 'Bonus Value & Fairness', weight: 'High', Icon: FaGift },
  { label: 'Game Library Breadth', weight: 'High', Icon: FaGamepad },
  { label: 'Mobile Experience', weight: 'Medium', Icon: FaMobileScreenButton },
  { label: 'Customer Support Quality', weight: 'Medium', Icon: FaHeadset },
  { label: 'Software Providers', weight: 'Medium', Icon: FaGear },
  { label: 'Security & Data Protection', weight: 'Medium', Icon: FaLock },
  { label: 'Transparency & T&C Clarity', weight: 'Medium', Icon: FaFileLines },
  { label: 'User Interface & UX', weight: 'Low', Icon: FaDesktop },
  { label: 'Complaints Track Record', weight: 'Low', Icon: FaChartLine },
];

export default function HowItWorks() {
  return (
    <>
      <Helmet>
        <title>How It Works — Our Casino Review Process | London Casino Deals</title>
        <meta name="description" content="Learn how London Casino Deals reviews UK casino sites. We follow a transparent 12-criteria methodology, verify UKGC licences, and test every site ourselves." />
        <meta property="og:title" content="How London Casino Deals Reviews UK Casinos — Our Methodology" />
        <meta property="og:description" content="Transparent, independent casino reviews. We verify licences, test real accounts, and score on 12 criteria including safety tools and responsible gambling." />
        <link rel="canonical" href="https://londoncasinodeals.com/how-it-works" />
      </Helmet>

      <div className="inner-hero">
        <div className="container">
          <div className="inner-hero__breadcrumb">
            <a href="/">Home</a> <span aria-hidden="true">›</span> How It Works
          </div>
          <h1 className="inner-hero__title">How London Casino Deals Reviews Casinos</h1>
          <p className="inner-hero__subtitle">
            Our editorial process is transparent, consistent, and fully independent from casino operators
          </p>
        </div>
      </div>

      <div className="inner-page-rg-slot">
        <div className="container">
          <RgCallout variant="compact" />
        </div>
      </div>

      <section className="section section--white">
        <div className="container">
          <div className="inner-intro">
            <h2>Why Our Reviews Are Independent</h2>
            <p>
              London Casino Deals earns affiliate commission when readers sign up at a recommended casino. This business model is common across comparison sites. However, we maintain a strict separation between editorial decisions and commercial relationships. Our ratings are determined by our review criteria alone — not by commission rates.
            </p>
            <p>
              No casino operator can pay to be featured, alter a score, or remove a negative finding from our reviews. If a site scores poorly across our criteria, it does not appear on this portal — regardless of the commercial arrangement.
            </p>
          </div>
          <RgCallout variant="boxed" className="inner-intro-rg" />
        </div>
      </section>

      <section className="section section--darker">
        <div className="container">
          <div className="section__header">
            <span className="section__eyebrow">The Process</span>
            <h2 className="section__title">Our 6-Step Review Process</h2>
          </div>
          <div className="steps-list">
            {STEPS.map((step) => (
              <div key={step.number} className="step-item">
                <div className="step-item__number">{step.number}</div>
                <div className="step-item__content">
                  <h3 className="step-item__title">{step.title}</h3>
                  <p className="step-item__text">{step.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--white">
        <div className="container">
          <div className="section__header">
            <span className="section__eyebrow">Scoring Model</span>
            <h2 className="section__title">Our 12-Criteria Scoring Framework</h2>
            <p className="section__subtitle">Each criterion carries a weighting that reflects its importance to player safety and experience</p>
          </div>
          <div className="criteria-table">
            {CRITERIA.map((c) => (
              <div key={c.label} className="criteria-row">
                <span className="criteria-row__icon" aria-hidden="true">
                  <c.Icon size={18} />
                </span>
                <span className="criteria-row__label">{c.label}</span>
                <span className={`criteria-row__weight criteria-row__weight--${c.weight.toLowerCase().replace(/\s+/g, '-')}`}>
                  {c.weight}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--darker">
        <div className="container">
          <RgCallout variant="compact" className="inner-page-rg-between" />
          <div className="editorial-commitment">
            <h2>Our Editorial Commitment</h2>
            <div className="commitment-grid">
              {[
                'We only recommend UKGC-licensed casinos',
                'We disclose commercial relationships clearly',
                'We test every site with real money before reviewing',
                'We update reviews when terms or services change',
                'We include responsible gambling tools in every review',
                'We do not allow operators to influence scores',
              ].map((text) => (
                <div key={text} className="commitment-item">
                  <span className="commitment-item__icon" aria-hidden="true">
                    <FaCircleCheck size={18} />
                  </span>
                  <span>{text}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="section__cta-wrap" style={{ marginTop: 40 }}>
            <Link to="/" className="btn btn--primary">Browse Our Casino Reviews</Link>
          </div>
        </div>
      </section>
    </>
  );
}
